import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PersonalizedProductsModelNames } from 'types/graphql';

import {
  useProductRecommendations,
  GetRecommendations,
  GetRecommendationsFallback,
  Options,
} from './use-product-recommendations';

import { getDispensaryFilter } from './recommendations.utils';
import { Scenarios } from './recommendations.types';
import { usePersonalizedItemsV2 } from './use-personalized-items-v2';

export function useSimilarItems(
  getRecommendationsFallback: GetRecommendationsFallback,
  itemId: string,
  scenario: Scenarios,
  options: Options
): ReturnType<typeof useProductRecommendations> {
  const flags = useFlags();
  const usePersonalizedProductsV2Flag = flags['growth.platform.use-personalized-products-v2.rollout'];

  // Always call hooks in the same order
  const { limit, skip } = options;

  const getRecommendations = useCallback<GetRecommendations>(
    (client, dispensary) =>
      client.getSimilarItems(itemId, {
        scenario,
        amt: limit,
        filters: [getDispensaryFilter(dispensary)],
      }),
    [itemId, scenario, limit]
  );

  const originalResult = useProductRecommendations(getRecommendations, getRecommendationsFallback, {
    ...options,
    excludeProducts: [itemId],
    skip: !!skip || usePersonalizedProductsV2Flag, // Skip original when V2 flag is enabled
  });

  // Use the existing V2 hook with relatedItems model for similar items
  const v2Result = usePersonalizedItemsV2(
    getRecommendationsFallback,
    [itemId],
    PersonalizedProductsModelNames.relatedItems,
    {
      ...options,
      skip: !!skip || !usePersonalizedProductsV2Flag,
    }
  );

  // Return the appropriate result based on feature flag
  return usePersonalizedProductsV2Flag ? v2Result : originalResult;
}
